import { queryOptions } from '@tanstack/react-query';
import {
  getInventoryPackages,
  getTransactions,
  getSalesSummary,
  getBrands,
  listPurchaseOrders,
  getPurchaseOrder,
} from './endpoints';
import type { TransactionItem, InventoryFilters, PurchaseOrderListParams } from './types';

const TRANSACTIONS_PAGE_SIZE = 500;

/**
 * Builds a GraphQL `PackageFilterInput` `where` clause from user-facing filters.
 * Multiple filters compose via `and`.
 */
function buildWhereClause(filters: InventoryFilters): Record<string, unknown> {
  const conditions: Record<string, unknown>[] = [];

  if (filters.brand_name) {
    conditions.push({
      product: { brand: { brandName: { contains: filters.brand_name } } },
    });
  }

  if (filters.product_search) {
    conditions.push({
      product: { whseProductsDescription: { contains: filters.product_search } },
    });
  }

  if (filters.category) {
    conditions.push({
      product: { productTypeNavigation: { productType: { contains: filters.category } } },
    });
  }

  if (conditions.length === 0) {
    return {};
  }
  if (conditions.length === 1) {
    return conditions[0]!;
  }
  return { and: conditions };
}

export const inventoryQuery = (filters?: InventoryFilters) => {
  const whereClause = filters ? buildWhereClause(filters) : {};
  return queryOptions({
    queryKey: filters ? (['inventory', { filters }] as const) : (['inventory'] as const),
    queryFn: () => getInventoryPackages(whereClause),
    staleTime: Infinity,
  });
};

export const transactionsQuery = (fromDate: string, toDate: string) =>
  queryOptions({
    queryKey: ['transactions', { fromDate, toDate }] as const,
    // The transactions endpoint caps responses at 500 rows. A 14-day window
    // for a busy dispensary easily exceeds that, so we paginate through all
    // pages and flatten into a single array. The complete set is cached as one
    // entry so velocity calculations across tools always use full data.
    queryFn: async () => {
      const allItems: TransactionItem[] = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const response = await getTransactions({
          PageSize: TRANSACTIONS_PAGE_SIZE,
          currentPage,
          Sorting: [{ Key: 'TransactionDate', Direction: 'DESC' }],
          Filters: [
            {
              Key: 'TransactionDate',
              StartValue: fromDate,
              EndValue: toDate,
              Operator: 'Between',
            },
          ],
          OnlySoldTransactions: true,
        });

        const page = response.Data;
        allItems.push(...page.Data);
        totalPages = page.TotalPages;
        currentPage++;
      } while (currentPage <= totalPages);

      return allItems;
    },
    staleTime: Infinity,
  });

export const salesSummaryQuery = (locIds: number[], fromDate: string, toDate: string) =>
  queryOptions({
    queryKey: ['sales-summary', { locIds, fromDate, toDate }] as const,
    queryFn: async () =>
      (
        await getSalesSummary({
          LocIds: locIds,
          DateRange: { FromDate: fromDate, ToDate: toDate },
        })
      ).Data,
    staleTime: 5 * 60 * 1000,
  });

export const brandsQuery = () =>
  queryOptions({
    queryKey: ['brands'] as const,
    queryFn: () => getBrands(),
    staleTime: Infinity,
  });

export const purchaseOrdersQuery = (params: PurchaseOrderListParams = {}) =>
  queryOptions({
    queryKey: ['purchase-orders', params] as const,
    queryFn: async () => {
      const res = await listPurchaseOrders(params);
      return { orders: res.data, totalCount: res.meta.totalCount };
    },
    staleTime: 30_000,
  });

export const purchaseOrderQuery = (id: string) =>
  queryOptions({
    queryKey: ['purchase-order', id] as const,
    queryFn: async () => (await getPurchaseOrder(id)).data.attributes,
    staleTime: 30_000,
  });
